import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Smartphone,
  Monitor,
  Tablet,
  Loader2,
  RefreshCw,
  ShieldCheck,
  X,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

function deviceIcon(label) {
  const s = (label || "").toLowerCase();
  if (s.includes("iphone") || s.includes("android")) return Smartphone;
  if (s.includes("ipad")) return Tablet;
  return Monitor;
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString();
}

export default function Sessions() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [signingOutAll, setSigningOutAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const revokeOne = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/auth/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      toast.success("Signed that device out");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke");
    } finally {
      setBusyId(null);
    }
  };

  const revokeAllOther = async () => {
    setSigningOutAll(true);
    try {
      const { data } = await api.post("/auth/sessions/revoke-all-other");
      setSessions((s) => s.filter((x) => x.is_current));
      toast.success(
        data?.revoked_count
          ? `Signed out of ${data.revoked_count} other device${data.revoked_count > 1 ? "s" : ""}`
          : "No other devices were signed in"
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign out other devices");
    } finally {
      setSigningOutAll(false);
      setConfirming(false);
    }
  };

  if (!user) return null;

  const others = sessions.filter((s) => !s.is_current);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="sessions-page">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav("/app")}
              data-testid="sessions-back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div className="w-px h-6 bg-zinc-200" />
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-zinc-700" />
              <p className="font-heading text-lg tracking-tight">Active sessions</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="sessions-refresh-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <p className="text-zinc-600 text-base leading-relaxed mb-8">
          These are the devices currently signed in to your Clinico Translate account.
          See something you don't recognise? Sign that device out — or revoke everything
          except the device you're using right now.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="sessions-loading">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-zinc-200 rounded-xl" data-testid="sessions-empty">
            <Monitor className="w-10 h-10 mx-auto text-zinc-300 mb-4" />
            <p className="font-heading text-xl text-zinc-900">No active sessions tracked</p>
            <p className="text-sm text-zinc-500 mt-2">
              If you signed in before May 25 2026 your existing session is still valid but
              not yet tracked here. Sign out and back in to start tracking this device.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="sessions-list">
            {sessions.map((s) => {
              const Icon = deviceIcon(s.device_label);
              return (
                <li
                  key={s.id}
                  className={`border rounded-xl p-4 flex items-start gap-4 ${
                    s.is_current ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 bg-white"
                  }`}
                  data-testid={`session-row-${s.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-zinc-900 text-white flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-zinc-900">{s.device_label}</p>
                      {s.is_current && (
                        <span
                          className="text-[10px] uppercase tracking-[0.15em] bg-zinc-900 text-white px-2 py-0.5 rounded"
                          data-testid="session-current-badge"
                        >
                          This device
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-zinc-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                      <span>Last active {relativeTime(s.last_seen_at)}</span>
                      {s.ip && <span className="font-mono text-xs">IP {s.ip}</span>}
                      <span className="text-zinc-400">Signed in {relativeTime(s.created_at)}</span>
                    </div>
                  </div>
                  {!s.is_current && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => revokeOne(s.id)}
                      disabled={busyId === s.id}
                      data-testid={`session-revoke-${s.id}`}
                    >
                      {busyId === s.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <>
                          <X className="w-3.5 h-3.5 mr-1" /> Sign out
                        </>
                      )}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {others.length > 0 && (
          <div className="mt-10 border border-zinc-200 rounded-xl p-5" data-testid="sessions-revoke-all-card">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading text-base text-zinc-900">Sign out of all other devices</p>
                <p className="text-sm text-zinc-600 mt-1">
                  Useful if you lost a phone or laptop, or are leaving a shared workstation.
                  This device will stay signed in.
                </p>
              </div>
            </div>
            {confirming ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="sessions-revoke-all-confirm">
                <Button
                  variant="default"
                  onClick={revokeAllOther}
                  disabled={signingOutAll}
                  data-testid="sessions-revoke-all-confirm-button"
                >
                  {signingOutAll ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing out…
                    </>
                  ) : (
                    `Yes, sign out ${others.length} other device${others.length > 1 ? "s" : ""}`
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfirming(false)}
                  disabled={signingOutAll}
                  data-testid="sessions-revoke-all-cancel"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setConfirming(true)}
                data-testid="sessions-revoke-all-button"
              >
                Sign out of all other devices
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
